import twConfig from '../../../tailwind.config';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';

export type ScreenBreakpoint = keyof typeof twConfig.theme.screens;

export interface BreakpointHook {
  /**
   * The current breakpoint that the window is in.
   */
  currentBreakpoint: ScreenBreakpoint;

  /**
   * Returns true if the current breakpoint is between the provided start and end breakpoints (inclusive).
   * @param start
   * @param end
   */
  isBetweenBreakpoints: (
    start: ScreenBreakpoint,
    end: ScreenBreakpoint,
  ) => boolean;

  /**
   * Returns true if the current breakpoint is above or equal to the provided breakpoint.
   * @param breakpoint
   */
  isAboveOrEqualToBreakpoint: (breakpoint: ScreenBreakpoint) => boolean;

  /**
   * Returns true if the current breakpoint is above the provided breakpoint.
   * @param breakpoint
   */
  isBelowOrEqualToBreakpoint: (breakpoint: ScreenBreakpoint) => boolean;

  /**
   * Returns true if the current breakpoint is above or equal to the provided breakpoint.
   * @param breakpoint
   */
  isAboveBreakpoint: (breakpoint: ScreenBreakpoint) => boolean;

  /**
   * Returns true if the current breakpoint is below the provided breakpoint.
   * @param breakpoint
   */
  isBelowBreakpoint: (breakpoint: ScreenBreakpoint) => boolean;
}

export const guardBreakpointRange = (
  start: ScreenBreakpoint,
  end: ScreenBreakpoint,
) => {
  const breakpoints = Object.keys(twConfig.theme.screens);
  if (!breakpoints.includes(start) || !breakpoints.includes(end)) {
    throw new Error(
      `Invalid breakpoint range: ${start} to ${end}. Available breakpoints: ${breakpoints.join(
        ', ',
      )}`,
    );
  }

  // Make sure that start is smaller than (or equal to) end
  if (breakpoints.indexOf(start) > breakpoints.indexOf(end)) {
    throw new Error(
      `Invalid breakpoint range: ${start} to ${end}. Start must be smaller than or equal to end.`,
    );
  }
};

const getWindowCurrentBreakpoint = (): ScreenBreakpoint => {
  const width = window.innerWidth;
  const keys = Object.keys(twConfig.theme.screens) as ScreenBreakpoint[];

  for (let index = 0; index < keys.length; index++) {
    const isLast = index === keys.length - 1;
    const boundaryMin = parseInt(twConfig.theme.screens[keys[index]], 10);
    const boundaryMax = isLast
      ? null
      : parseInt(twConfig.theme.screens[keys[index + 1]], 10) - 1;

    if (
      width >= boundaryMin &&
      (boundaryMax === null || width <= boundaryMax)
    ) {
      return keys[index];
    }
  }

  // Mobile first, incase the window is smaller than the smallest breakpoint. This should never happen.
  uiAppLogger.warn(
    `Window size is smaller than the smallest breakpoint. Defaulting to the smallest breakpoint.`,
  );

  return Object.keys(twConfig.theme.screens)[0] as ScreenBreakpoint;
};

export const useBreakpoints = (): BreakpointHook => {
  // Let's compute the initial state at runtime for the best possible accuracy.
  const [currentBreakpoint, setCurrentBreakpoint] = useState<ScreenBreakpoint>(
    getWindowCurrentBreakpoint,
  );

  // Capture changes to the current breakpoint.
  useEffect(() => {
    const handleResize = () => {
      const newBreakpoint = getWindowCurrentBreakpoint();

      if (newBreakpoint !== currentBreakpoint) {
        setCurrentBreakpoint(getWindowCurrentBreakpoint);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentBreakpoint]);

  const isBetweenBreakpoints = useCallback(
    (start: ScreenBreakpoint, end: ScreenBreakpoint) => {
      guardBreakpointRange(start, end);

      const startIndex = Object.keys(twConfig.theme.screens).indexOf(start);
      const endIndex = Object.keys(twConfig.theme.screens).indexOf(end);
      const currentIndex = Object.keys(twConfig.theme.screens).indexOf(
        currentBreakpoint,
      );

      return currentIndex >= startIndex && currentIndex <= endIndex;
    },
    [currentBreakpoint],
  );

  const isAboveOrEqualToBreakpoint = useCallback(
    (breakpoint: ScreenBreakpoint) => {
      const currentIndex = Object.keys(twConfig.theme.screens).indexOf(
        currentBreakpoint,
      );
      const targetIndex = Object.keys(twConfig.theme.screens).indexOf(
        breakpoint,
      );

      return currentIndex >= targetIndex;
    },
    [currentBreakpoint],
  );

  const isBelowOrEqualToBreakpoint = useCallback(
    (breakpoint: ScreenBreakpoint) => {
      const currentIndex = Object.keys(twConfig.theme.screens).indexOf(
        currentBreakpoint,
      );
      const targetIndex = Object.keys(twConfig.theme.screens).indexOf(
        breakpoint,
      );

      return currentIndex <= targetIndex;
    },
    [currentBreakpoint],
  );

  const isAboveBreakpoint = useCallback(
    (breakpoint: ScreenBreakpoint) => {
      const currentIndex = Object.keys(twConfig.theme.screens).indexOf(
        currentBreakpoint,
      );
      const targetIndex = Object.keys(twConfig.theme.screens).indexOf(
        breakpoint,
      );

      return currentIndex > targetIndex;
    },
    [currentBreakpoint],
  );

  const isBelowBreakpoint = useCallback(
    (breakpoint: ScreenBreakpoint) => {
      const currentIndex = Object.keys(twConfig.theme.screens).indexOf(
        currentBreakpoint,
      );
      const targetIndex = Object.keys(twConfig.theme.screens).indexOf(
        breakpoint,
      );

      return currentIndex < targetIndex;
    },
    [currentBreakpoint],
  );

  return useMemo(() => {
    return {
      currentBreakpoint,
      isBetweenBreakpoints,
      isAboveBreakpoint,
      isBelowBreakpoint,
      isAboveOrEqualToBreakpoint,
      isBelowOrEqualToBreakpoint,
    };
  }, [
    currentBreakpoint,
    isAboveBreakpoint,
    isAboveOrEqualToBreakpoint,
    isBelowBreakpoint,
    isBelowOrEqualToBreakpoint,
    isBetweenBreakpoints,
  ]);
};
